import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef } from "@mui/x-data-grid";
import { AppDataTable, AppPageHeader, AppSearchField, AppToolbar } from "../components/common";
import {
  fetchVehicleRentUpdateBatches,
  fetchVehicleRentUpdateItems,
  VehicleRentUpdateBatch,
  VehicleRentUpdateItem,
} from "../services/vehicleHistoryService";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

function formatRentDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
}

const columns: GridColDef[] = [
  { field: "publicRiderId", headerName: "Rider ID", minWidth: 125, flex: 0.8 },
  {
    field: "riderName",
    headerName: "Rider",
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => params.row.riderName || "—",
  },
  { field: "registrationNumber", headerName: "Vehicle", minWidth: 130, flex: 0.9 },
  { field: "clientName", headerName: "Client", minWidth: 145, flex: 1 },
  { field: "clientRiderId", headerName: "Client Rider ID", minWidth: 130, flex: 0.9 },
  {
    field: "weekStartDate",
    headerName: "Rent Week",
    minWidth: 185,
    flex: 1,
    renderCell: ({ row }) => `${formatRentDate(row.weekStartDate)} – ${formatRentDate(row.weekEndDate)}`,
  },
  { field: "numberOfDays", headerName: "Days", minWidth: 75, type: "number", align: "right", headerAlign: "right" },
  {
    field: "totalRent",
    headerName: "Rent Updated",
    minWidth: 135,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueFormatter: (params) => currencyFormatter.format(Number(params.value ?? 0)),
  },
  { field: "assignmentCount", headerName: "Assignments", minWidth: 105, type: "number", align: "right", headerAlign: "right" },
  {
    field: "beforeRentAmount",
    headerName: "Previous Total",
    minWidth: 135,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueFormatter: (params) => currencyFormatter.format(Number(params.value ?? 0)),
  },
  {
    field: "afterRentAmount",
    headerName: "New Total",
    minWidth: 135,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueFormatter: (params) => currencyFormatter.format(Number(params.value ?? 0)),
  },
  {
    field: "batchId",
    headerName: "Batch",
    minWidth: 150,
    flex: 0.9,
    renderCell: ({ value }) => (
      <Typography variant="caption" title={String(value)} noWrap>
        {String(value).slice(0, 8)}
      </Typography>
    ),
  },
  {
    field: "batchCreatedAt",
    headerName: "Batch Created",
    minWidth: 165,
    flex: 1,
    valueFormatter: (params) => formatDateTime(String(params.value ?? "")),
  },
  { field: "updatedBy", headerName: "Updated By", minWidth: 170, flex: 1 },
  {
    field: "createdAt",
    headerName: "Updated At",
    minWidth: 165,
    flex: 1,
    valueFormatter: (params) => formatDateTime(String(params.value ?? "")),
  },
];

const batchColumns: GridColDef[] = [
  {
    field: "weekStartDate",
    headerName: "Rent Week",
    minWidth: 200,
    flex: 1,
    renderCell: ({ row }) => `${formatRentDate(row.weekStartDate)} – ${formatRentDate(row.weekEndDate)}`,
  },
  { field: "updatedBy", headerName: "Updated By", minWidth: 170, flex: 1 },
  {
    field: "createdAt",
    headerName: "Created At",
    minWidth: 165,
    flex: 1,
    valueFormatter: (params) => formatDateTime(String(params.value ?? "")),
  },
  {
    field: "sourceFileUrl",
    headerName: "Source File",
    minWidth: 130,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: ({ value }) =>
      typeof value === "string" ? (
        <Link href={value} target="_blank" rel="noopener noreferrer" underline="hover">
          View file
        </Link>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Unavailable
        </Typography>
      ),
  },
];

interface VehicleRentUpdateItemsProps {
  onClose: () => void;
}

const VehicleRentUpdateItems = ({ onClose }: VehicleRentUpdateItemsProps) => {
  const [activeTab, setActiveTab] = useState<"batches" | "audit">("batches");
  const [rows, setRows] = useState<VehicleRentUpdateItem[]>([]);
  const [batches, setBatches] = useState<VehicleRentUpdateBatch[]>([]);
  const [page, setPage] = useState(0);
  const [batchPage, setBatchPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [batchPageSize, setBatchPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [search, setSearch] = useState("");
  const [weekStartDateFrom, setWeekStartDateFrom] = useState("");
  const [weekStartDateTo, setWeekStartDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const latestRequest = useRef(0);
  const latestBatchesRequest = useRef(0);

  const loadItems = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVehicleRentUpdateItems({
        page: page + 1,
        pageSize,
        search: search.trim() || undefined,
        weekStartDateFrom: weekStartDateFrom || undefined,
        weekStartDateTo: weekStartDateTo || undefined,
      });
      if (requestId !== latestRequest.current) return;
      setRows(result.data);
      setTotal(result.pagination.total);
    } catch (loadError) {
      if (requestId !== latestRequest.current) return;
      setError(loadError instanceof Error ? loadError.message : "Unable to load rent update records.");
      setRows([]);
      setTotal(0);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [page, pageSize, search, weekStartDateFrom, weekStartDateTo, reloadKey]);

  const loadBatches = useCallback(async () => {
    const requestId = ++latestBatchesRequest.current;
    setBatchesLoading(true);
    setBatchesError(null);
    try {
      const result = await fetchVehicleRentUpdateBatches({
        page: batchPage + 1,
        pageSize: batchPageSize,
        weekStartDateFrom: weekStartDateFrom || undefined,
        weekStartDateTo: weekStartDateTo || undefined,
      });
      if (requestId !== latestBatchesRequest.current) return;
      setBatches(result.data);
      setTotalBatches(result.pagination.total);
    } catch (loadError) {
      if (requestId !== latestBatchesRequest.current) return;
      setBatchesError(
        loadError instanceof Error ? loadError.message : "Unable to load rent update batches.",
      );
      setBatches([]);
      setTotalBatches(0);
    } finally {
      if (requestId === latestBatchesRequest.current) setBatchesLoading(false);
    }
  }, [batchPage, batchPageSize, weekStartDateFrom, weekStartDateTo, reloadKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadItems(), search ? 300 : 0);
    return () => {
      window.clearTimeout(timer);
      latestRequest.current += 1;
    };
  }, [loadItems, search]);

  useEffect(() => {
    void loadBatches();
    return () => {
      latestBatchesRequest.current += 1;
    };
  }, [loadBatches]);

  const clearFilters = () => {
    setSearch("");
    setWeekStartDateFrom("");
    setWeekStartDateTo("");
    setPage(0);
    setBatchPage(0);
  };

  const filtersActive = Boolean(search || weekStartDateFrom || weekStartDateTo);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "calc(100vh - 96px)" }}>
      <Stack spacing={2.5}>
        <AppPageHeader
          title="Vehicle Rent Updates"
          subtitle="Review rent update audit items, rider totals, and the batch that applied each update."
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setReloadKey((value) => value + 1)}
                disabled={loading || batchesLoading}
              >
                Refresh
              </Button>
              <Button variant="text" startIcon={<CloseIcon />} onClick={onClose}>
                Close
              </Button>
            </Stack>
          }
        />

        <AppToolbar
          left={
            activeTab === "audit" ? (
              <AppSearchField
                value={search}
                fullWidth
                placeholder="Search rider, vehicle, client, batch, or updater..."
                onChange={(value) => {
                  setSearch(value);
                  setPage(0);
                }}
              />
            ) : null
          }
          right={
            <>
              <TextField
                size="small"
                type="date"
                label="Week starts from"
                value={weekStartDateFrom}
                onChange={(event) => {
                  setWeekStartDateFrom(event.target.value);
                  setPage(0);
                  setBatchPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: "100%", sm: 165 } }}
              />
              <TextField
                size="small"
                type="date"
                label="Week starts to"
                value={weekStartDateTo}
                onChange={(event) => {
                  setWeekStartDateTo(event.target.value);
                  setPage(0);
                  setBatchPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: "100%", sm: 165 } }}
              />
              <Button
                variant="text"
                onClick={clearFilters}
                disabled={!filtersActive}
                sx={{ whiteSpace: "nowrap" }}
              >
                Clear filters
              </Button>
            </>
          }
        />

        <Tabs
          value={activeTab}
          onChange={(_, value: "batches" | "audit") => setActiveTab(value)}
          aria-label="Vehicle rent update views"
        >
          <Tab label="Rent update batches" value="batches" id="rent-update-tab-batches" aria-controls="rent-update-panel-batches" />
          <Tab label="Rent update audit records" value="audit" id="rent-update-tab-audit" aria-controls="rent-update-panel-audit" />
        </Tabs>

        {activeTab === "batches" ? (
          <Stack
            spacing={2}
            role="tabpanel"
            id="rent-update-panel-batches"
            aria-labelledby="rent-update-tab-batches"
          >
            {batchesError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => setReloadKey((value) => value + 1)}>
                    Retry
                  </Button>
                }
              >
                {batchesError}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {batchesLoading
                ? "Loading rent update batches…"
                : `${totalBatches.toLocaleString("en-IN")} batches`}
            </Typography>
            <AppDataTable
              rows={batches}
              columns={batchColumns}
              loading={batchesLoading}
              rowCount={totalBatches}
              page={batchPage}
              pageSize={batchPageSize}
              onPageChange={setBatchPage}
              onPageSizeChange={(size) => {
                setBatchPage(0);
                setBatchPageSize(size);
              }}
              getRowId={(row) => row.id}
            />
          </Stack>
        ) : (
          <Stack
            spacing={2}
            role="tabpanel"
            id="rent-update-panel-audit"
            aria-labelledby="rent-update-tab-audit"
          >
            {error && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => setReloadKey((value) => value + 1)}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {loading ? "Loading rent update records…" : `${total.toLocaleString("en-IN")} audit records`}
            </Typography>
            <AppDataTable
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPage(0);
                setPageSize(size);
              }}
              getRowId={(row) => row.id}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default VehicleRentUpdateItems;
